// Тач-управление крестиком графика на мобильных (как на TradingView):
// горизонтальный драг пальцем или удержание (long-press) двигают крестик и тултип
// по точкам графика, вертикальный свайп остаётся обычным скроллом страницы.
// Chart.js слушает только mousemove/mouseout/click, поэтому touchmove обрабатываем сами.

const DIRECTION_LOCK_PX = 6 // сдвиг, после которого решаем: драг по графику или скролл
const HOLD_MS = 300 // удержание пальца без движения включает крестик

export default function attachTouchCrosshair(chart) {
  const { canvas } = chart
  let start = null // позиция touchstart относительно canvas
  let mode = null // null — направление ещё не выбрано | 'crosshair' | 'scroll'
  let holdTimer = null
  let shownByTouch = false // крестик показан нашим жестом, а не click'ом Chart.js

  const relativePosition = touch => {
    const rect = canvas.getBoundingClientRect()
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top }
  }

  const moveCrosshair = (position, native) => {
    // native в объекте — сигнал для Chart.js, что координаты уже относительно canvas
    const elements = chart.getElementsAtEventForMode(
      { x: position.x, y: position.y, native },
      'index',
      { intersect: false },
      true
    )
    if (!elements.length) return
    const active = elements.map(({ datasetIndex, index }) => ({
      datasetIndex,
      index,
    }))
    chart.setActiveElements(active)
    chart.tooltip.setActiveElements(active, position)
    chart.render()
    shownByTouch = true
  }

  // Прячем крестик и тултип (Chart.js вызовет external-тултип с opacity 0)
  const clearCrosshair = () => {
    shownByTouch = false
    chart.setActiveElements([])
    chart.tooltip.setActiveElements([], { x: 0, y: 0 })
    chart.render()
  }

  const clearHold = () => {
    if (holdTimer) clearTimeout(holdTimer)
    holdTimer = null
  }

  const reset = () => {
    clearHold()
    start = null
    mode = null
  }

  const onTouchStart = event => {
    if (event.touches.length !== 1) {
      reset()
      return
    }
    start = relativePosition(event.touches[0])
    mode = null
    clearHold()
    holdTimer = setTimeout(() => {
      holdTimer = null
      if (mode !== null || !start) return
      mode = 'crosshair'
      moveCrosshair(start, event)
    }, HOLD_MS)
  }

  const onTouchMove = event => {
    if (!start || event.touches.length !== 1) return
    const position = relativePosition(event.touches[0])

    if (mode === null) {
      const dx = Math.abs(position.x - start.x)
      const dy = Math.abs(position.y - start.y)
      if (dx < DIRECTION_LOCK_PX && dy < DIRECTION_LOCK_PX) return
      clearHold()
      if (dx <= dy) {
        mode = 'scroll'
        return
      }
      mode = 'crosshair'
    }

    if (mode !== 'crosshair') return
    if (event.cancelable) event.preventDefault() // не скроллим страницу, пока ведём крестик
    moveCrosshair(position, event)
  }

  const onTouchEnd = event => {
    // после драга/удержания не нужны эмулированные mouse-события браузера;
    // обычный тап (mode === null) оставляем как есть — он даёт click и тултип по клику
    if (mode === 'crosshair' && event.cancelable) event.preventDefault()
    reset()
  }

  const onTouchCancel = () => reset()

  // После тапа по canvas браузер сам шлёт mouseout при касании в другом месте,
  // и Chart.js прячет тултип. После драга/удержания mouse-событий нет — прячем сами.
  const onDocumentTouchStart = event => {
    if (!shownByTouch || event.target === canvas) return
    clearCrosshair()
  }

  canvas.addEventListener('touchstart', onTouchStart, { passive: true })
  canvas.addEventListener('touchmove', onTouchMove, { passive: false })
  canvas.addEventListener('touchend', onTouchEnd, { passive: false })
  canvas.addEventListener('touchcancel', onTouchCancel, { passive: true })
  document.addEventListener('touchstart', onDocumentTouchStart, {
    passive: true,
    capture: true,
  })

  return function detach() {
    reset()
    canvas.removeEventListener('touchstart', onTouchStart)
    canvas.removeEventListener('touchmove', onTouchMove)
    canvas.removeEventListener('touchend', onTouchEnd)
    canvas.removeEventListener('touchcancel', onTouchCancel)
    document.removeEventListener('touchstart', onDocumentTouchStart, {
      capture: true,
    })
  }
}
